import { match, P } from 'ts-pattern';
import api from '~/utils/api';
import { identity } from '~/utils/compute';
import {
  ExpiredSessionError,
  ResponseError,
  StoreNotFoundError,
} from '~/utils/errors';
import { addBreadcrumb } from '~/utils/observability';
import { MiddlewareFunction } from '~/utils/routing';
import { storeParamSchema } from '~/utils/schemas';
import { defaultTheme, setTheme } from '~/utils/theme';

/**
 * Middleware for the app shell route runs before all loaders.
 * Sets the theme and in the future may initialize the store.
 * The context is passed as handler context to all data functions
 * and can be used to share data/state.
 */
export const middleware: MiddlewareFunction = async ({ params }, ctx) => {
  const { appStore } = ctx;
  try {
    const parsedParams = storeParamSchema.safeParse(params);
    const state = appStore.getState();

    if (state.status === 'expired') {
      appStore.dispatch(appStore.actions.reset());
      throw new ExpiredSessionError();
    }
    // when on a crew route with store and slug does not match state, get the store settings and reset the app state
    if (
      parsedParams.success &&
      parsedParams.data.store !== state.settings?.storeSlug
    ) {
      addBreadcrumb({
        type: 'debug',
        level: 'info',
        category: 'appMiddleware',
        message: 'Fetching store settings from API',
        data: {
          storeParam: parsedParams.data.store,
          settingsStoreSlug: state.settings?.storeSlug,
        },
      });

      const slug = parsedParams.data.store;
      const settings = await api
        .fetchSettings({
          params: { slug },
        })
        .catch((error) => {
          // if the store is not found, throw a specific error
          if (
            error instanceof ResponseError &&
            error.response?.status === 404
          ) {
            throw new StoreNotFoundError({ slug });
          }

          // rethrow other errors
          throw error;
        });

      appStore.dispatch(appStore.actions.reset({ settings }));
    }

    const theme =
      parsedParams.success ?
        (appStore.getState().settings?.theme ?? defaultTheme)
      : defaultTheme;

    setTheme(theme);

    return ctx;
  } catch (error) {
    const middlewareError = match(error)
      .with(P.instanceOf(ExpiredSessionError), identity)
      .with(P.instanceOf(StoreNotFoundError), identity)
      .with(P.instanceOf(ResponseError), identity)
      .otherwise(
        () =>
          new Error(
            `App Middleware failed: ${error instanceof Error ? error.message : 'unknown error'}`,
            { cause: error },
          ),
      );

    // abort the react router data loading and show an error page
    throw middlewareError;
  }
};
